import { ISemSimplifiedLpXP } from "@faire/web-api/indigofair/seo/ISemSimplifiedLpXP";
import { ENABLE_SEM_SIMPLIFIED_LP_TEST_101124 } from "@faire/web-api/indigofair/settings/ENABLE_SEM_SIMPLIFIED_LP_TEST_101124";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

import { useIsLoggedOutDiscoverPage } from "../views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPage";

import { useSetting } from "./__internal__/useSetting";

/**
 * @name checkIsSemTraffic
 * @description Determines if the current traffic is from paid search (SEM).
 *
 * @param searchParams - Next.js search params object
 * @returns boolean indicating if traffic is from paid search
 *
 * @note Checks for utm_medium=cpc in the URL query parameters,
 * which indicates the traffic is from paid search marketing campaigns.
 */
export const checkIsSemTraffic = (searchParams: ReturnType<typeof useSearchParams>): boolean => {
  return (searchParams || { get: () => null }).get("utm_medium") === "cpc";
};

/**
 * @name useIsSettingSemSimplifiedLpEnabled
 * @description Determines if user is a paid user
 * and if the SemSimplifiedLp is enabled.
 *
 * @note medium=cpc is the query parameter that is
 * used to determine if the user is a paid user.
 * We also need to determine if the user is on the discover
 * search page.
 */

export const useIsSettingSemSimplifiedLpEnabled = () => {
  const isLoggedOutDiscoverPage = useIsLoggedOutDiscoverPage();
  const setting = useSetting(
    ENABLE_SEM_SIMPLIFIED_LP_TEST_101124,
    ISemSimplifiedLpXP.Bucket.CONTROL
  );

  const searchParams = useSearchParams();

  return useMemo(() => {
    return (
      setting === ISemSimplifiedLpXP.Bucket.VARIANT_A &&
      checkIsSemTraffic(searchParams) &&
      isLoggedOutDiscoverPage
    );
  }, [isLoggedOutDiscoverPage, searchParams, setting]);
};
